import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import Sidebar from "../../components/feature/Sidebar";
import TopBar from "../../components/feature/TopBar";
import ApprovalList from "./components/ApprovalList";
import DocumentEditor from "./components/DocumentEditor";
import ApprovalDetail from "./components/ApprovalDetail";
import { approvalHistory } from "../../mocks/approvals";
import { useCompany } from "../../contexts/CompanyContext";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { logStatChange } from "../../lib/statHistory";

type ViewMode = "detail" | "form";

export default function Approval() {
  const [selectedApprovalId, setSelectedApprovalId] = useState<string | null>(null);
  const [activeTemplateId, setActiveTemplateId] = useState<string | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("detail");
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [history, setHistory] = useState(approvalHistory);
  const [resubmitInfo, setResubmitInfo] = useState<{ title: string; aiComment: string } | null>(null);
  const [resubmitOriginalId, setResubmitOriginalId] = useState<string | null>(null);
  const [resubmittedIds, setResubmittedIds] = useState<Set<string>>(new Set());
  const [lastStatChange, setLastStatChange] = useState<{ rep: number; cap: number } | null>(null);
  const { updateReputation, updateCapital, workspaceId } = useCompany();
  const { user } = useAuth();

  const handleNewForm = (templateId: string) => { setActiveTemplateId(templateId); setViewMode("form"); setSelectedApprovalId(null); setSubmitSuccess(false); setResubmitInfo(null); setResubmitOriginalId(null); };
  const handleSelectApproval = (id: string) => { setSelectedApprovalId(id); setViewMode("detail"); setActiveTemplateId(null); setSubmitSuccess(false); setResubmitInfo(null); setResubmitOriginalId(null); };
  const handleResubmit = (templateId: string, info: { title: string; aiComment: string; originalId: string }) => {
    setActiveTemplateId(templateId);
    setViewMode("form");
    setSelectedApprovalId(null);
    setSubmitSuccess(false);
    setResubmitInfo({ title: info.title, aiComment: info.aiComment });
    setResubmitOriginalId(info.originalId);
  };

  const location = useLocation();

  useEffect(() => {
    const state = location.state as { templateId?: string } | null;
    if (state?.templateId) {
      handleNewForm(state.templateId);
      window.history.replaceState({}, "");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFormSubmit = async (data: Record<string, string>) => {
    const titles: Record<string, string> = {
      tmpl1: "전속 계약서", tmpl2: "프로젝트 예산 기안서",
      tmpl3: "음원 발매 기안서", tmpl4: "법적 대응 기안서",
      tmpl10: "보도자료 배포 기안서",
    };
    const newApproval = {
      id: `ap${Date.now()}`,
      type: activeTemplateId === "tmpl1" ? "contract" : activeTemplateId === "tmpl2" ? "budget" : activeTemplateId === "tmpl3" ? "release" : "legal",
      title: data["f1"] ? `${titles[activeTemplateId || "tmpl1"]} - ${data["f1"]}` : titles[activeTemplateId || "tmpl1"],
      submittedBy: "직접 기안",
      submittedAt: new Date().toLocaleString("ko-KR"),
      status: "approved" as const,
      aiComment: "승인: AI 검토를 통과한 기안서입니다. 모든 조건이 적정 범위 내에 있습니다.",
      amount: parseInt(data["f4"] || data["f2"] || "0") * 10000 || 0,
      reputationChange: 5,
      capitalChange: -(parseInt(data["f4"] || data["f2"] || "0") || 0),
    };
    setHistory(prev => [newApproval, ...prev]);
    // DB 저장 — 강사 전자결재 관리 패널에서 열람/처리 가능
    if (workspaceId && user) {
      const docTypeMap: Record<string, string> = { tmpl1: "contract", tmpl2: "budget", tmpl3: "release", tmpl4: "legal", tmpl10: "marketing" };
      const formDataFiltered: Record<string, string> = {};
      Object.entries(data).forEach(([k, v]) => { if (v) formDataFiltered[k] = v; });
      await supabase.from("approval_docs").insert({
        workspace_id: workspaceId,
        user_id: user.id,
        doc_type: docTypeMap[activeTemplateId ?? ""] ?? "approval",
        title: newApproval.title,
        form_data: formDataFiltered,
        status: "pending",
        ai_comment: newApproval.aiComment,
        capital_change: newApproval.capitalChange,
      }).select().maybeSingle();
    }
    // 원본 반려 문서에 재기안 완료 표시
    if (resubmitOriginalId) {
      setResubmittedIds(prev => new Set([...prev, resubmitOriginalId]));
      setResubmitOriginalId(null);
    }
    // 보도자료 배포 기안서 승인 시 마케팅 성과 부스트 플래그 설정
    if (activeTemplateId === "tmpl10") {
      try {
        localStorage.setItem("kap_press_boost_chart", "1");
        localStorage.setItem("kap_press_boost_sns", "1");
        localStorage.setItem("kap_press_boost_title", data["f1"] ?? "신규 보도자료");
      } catch { /* ignore */ }
    }
    // ── 승인 시 실제 수치 반영 ──────────────────────
    // 명성 점수: 기본 +5, 보도자료/법적대응은 +8
    const repGain = (activeTemplateId === "tmpl10" || activeTemplateId === "tmpl4") ? 8 : 5;
    // 자본금: 예산 기안서/보도자료 배포 기안서만 차감 (입력값 만원 단위)
    const rawAmount = parseInt(data["f4"] || data["f2"] || "0") || 0;
    const capDelta = (activeTemplateId === "tmpl2" || activeTemplateId === "tmpl10") && rawAmount > 0 ? -rawAmount : 0;
    setLastStatChange({ rep: repGain, cap: capDelta });
    const approvalLabel = data["f1"] ? `${({"tmpl1":"전속 계약서","tmpl2":"예산 기안서","tmpl3":"발매 기안서","tmpl4":"법적 대응","tmpl10":"보도자료 배포"} as Record<string,string>)[activeTemplateId ?? ""] ?? "결재"} - ${data["f1"]}` : "결재 승인";
    logStatChange("reputation", repGain, approvalLabel);
    if (capDelta !== 0) logStatChange("capital", capDelta, approvalLabel);
    try {
      await updateReputation(repGain);
      if (capDelta !== 0) await updateCapital(capDelta);
    } catch { /* ignore if not connected */ }
    setSubmitSuccess(true);
    setTimeout(() => { setViewMode("detail"); setSelectedApprovalId(newApproval.id); setSubmitSuccess(false); setLastStatChange(null); }, 2800);
  };

  return (
    <div className="min-h-screen bg-[#0C0C0E] flex font-sans">
      <Sidebar />
      <div className="flex-1 ml-56 flex flex-col">
        <TopBar />
        <div className="flex-1 flex overflow-hidden" style={{ height: "calc(100vh - 48px)" }}>

          {/* List */}
          <div className="bg-[#111112] border-r border-white/[0.05] flex flex-col shrink-0" style={{ width: "270px" }}>
            <div className="px-4 py-3 border-b border-white/[0.05]">
              <span className="text-[#F2F2F2] text-sm font-medium">전자결재</span>
              <p className="text-[#3D3D3D] text-[10px] mt-0.5">{history.filter(h => h.status === "pending").length}건 처리 대기</p>
            </div>
            <div className="flex-1 overflow-hidden">
              <ApprovalList selectedId={selectedApprovalId} onSelect={handleSelectApproval} onNew={handleNewForm} resubmittedIds={resubmittedIds} />
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 bg-[#0C0C0E] flex flex-col overflow-hidden relative">
            {submitSuccess && (
              <div className="absolute inset-0 bg-[#0C0C0E]/90 z-10 flex items-center justify-center">
                <div className="text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-xl bg-[#4B6CF7]/15 border border-[#4B6CF7]/20 mx-auto mb-4">
                    <i className="ri-check-double-line text-[#7A9BFF] text-2xl"></i>
                  </div>
                  <p className="text-[#F2F2F2] font-semibold text-base">결재가 승인되었습니다</p>
                  <p className="text-[#555555] text-sm mt-1">AI 시스템이 처리했습니다</p>
                  {lastStatChange && (
                    <div className="mt-4 flex items-center justify-center gap-3 flex-wrap">
                      <div className="flex items-center gap-1.5 bg-amber-400/10 border border-amber-400/20 px-3 py-1.5 rounded-lg">
                        <i className="ri-award-fill text-amber-400 text-sm"></i>
                        <span className="text-amber-400 text-xs font-bold">직급 점수 +{lastStatChange.rep}</span>
                      </div>
                      {lastStatChange.cap < 0 && (
                        <div className="flex items-center gap-1.5 bg-red-400/10 border border-red-400/20 px-3 py-1.5 rounded-lg">
                          <i className="ri-coin-line text-red-400 text-sm"></i>
                          <span className="text-red-400 text-xs font-bold">자본금 {lastStatChange.cap.toLocaleString()}만원</span>
                        </div>
                      )}
                      {lastStatChange.cap === 0 && (
                        <div className="flex items-center gap-1.5 bg-[#4B6CF7]/10 border border-[#4B6CF7]/20 px-3 py-1.5 rounded-lg">
                          <i className="ri-shield-check-line text-[#7A9BFF] text-sm"></i>
                          <span className="text-[#7A9BFF] text-xs font-bold">대시보드 반영 완료</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
            {viewMode === "form" && activeTemplateId
              ? <DocumentEditor templateId={activeTemplateId} onSubmit={handleFormSubmit} onCancel={() => { setViewMode("detail"); setActiveTemplateId(null); setResubmitInfo(null); }} resubmitInfo={resubmitInfo ?? undefined} />
              : <ApprovalDetail approvalId={selectedApprovalId} onResubmit={handleResubmit} />
            }
          </div>
        </div>
      </div>
    </div>
  );
}
